import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Settings2, Loader2, KanbanSquare, ListTodo, Table2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermisosTareas, useResumenMias, useTablero, useTableros } from '@/hooks/useTareas';
import { KanbanBoard } from './KanbanBoard';
import { MisTareasView } from './MisTareasView';
import { ListaTareasView } from './ListaTareasView';
import { TareaFormDialog } from './TareaFormDialog';
import { TareaDetailModal } from './TareaDetailModal';
import { AdministrarTablerosDialog } from './AdministrarTablerosDialog';

type Vista = 'tableros' | 'mias' | 'lista';

const VISTAS: { id: Vista; label: string; icono: typeof KanbanSquare }[] = [
  { id: 'tableros', label: 'Tableros', icono: KanbanSquare },
  { id: 'mias', label: 'Mis tareas', icono: ListTodo },
  { id: 'lista', label: 'Lista', icono: Table2 },
];

const STORAGE_TABLERO = 'anik-tareas-tablero';

function leerTableroGuardado(): string | undefined {
  try { return localStorage.getItem(STORAGE_TABLERO) ?? undefined; } catch { return undefined; }
}

export function TareasPage() {
  const [params, setParams] = useSearchParams();
  const { esAdmin } = usePermisosTareas();
  const tareaAbiertaId = params.get('t');

  const [vista, setVista] = useState<Vista>(() => {
    const pedida = params.get('vista');
    return VISTAS.some(v => v.id === pedida) ? (pedida as Vista) : 'tableros';
  });
  const [tableroId, setTableroId] = useState<string | undefined>(leerTableroGuardado);
  const [nuevaTarea, setNuevaTarea] = useState<{ columnaId?: string } | null>(null);
  const [administrando, setAdministrando] = useState(false);

  const { data: tablerosData, isLoading: cargandoTableros } = useTableros();
  const tableros = (tablerosData?.tableros ?? []).filter(t => !t.archivado);

  // Si el tablero guardado ya no existe (archivado/eliminado) se cae al predeterminado.
  useEffect(() => {
    if (!tablerosData) return;
    if (!tableroId || !tableros.some(t => t.id === tableroId)) setTableroId(tablerosData.defaultId ?? undefined);
  }, [tablerosData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tableroId) return;
    try { localStorage.setItem(STORAGE_TABLERO, tableroId); } catch { /* almacenamiento no disponible */ }
  }, [tableroId]);

  const { data: tablero, isLoading: cargandoTablero, isError } = useTablero(vista === 'tableros' ? tableroId : undefined);
  const { data: resumen } = useResumenMias();

  const abrirTarea = (id: string) => setParams(prev => { const n = new URLSearchParams(prev); n.set('t', id); return n; }, { replace: true });
  const cerrarTarea = () => setParams(prev => { const n = new URLSearchParams(prev); n.delete('t'); return n; }, { replace: true });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-text-primary mr-auto">Tareas</h1>
        {vista === 'tableros' && tableros.length > 0 && (
          <select
            value={tableroId ?? ''}
            onChange={e => setTableroId(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            aria-label="Tablero"
          >
            {tableros.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        )}
        {esAdmin && (
          <button onClick={() => setAdministrando(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-text-primary hover:bg-gray-50">
            <Settings2 size={15} /> Administrar
          </button>
        )}
        <button
          onClick={() => setNuevaTarea({})}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
        >
          <Plus size={16} /> Nueva tarea
        </button>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist">
        {VISTAS.map(({ id, label, icono: Icono }) => (
          <button
            key={id}
            role="tab"
            aria-selected={vista === id}
            onClick={() => setVista(id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              vista === id ? 'border-primary-500 text-primary-700' : 'border-transparent text-text-secondary hover:text-text-primary',
            )}
          >
            <Icono size={15} /> {label}
            {id === 'mias' && resumen && resumen.vencidas > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px]">{resumen.vencidas}</span>
            )}
          </button>
        ))}
      </div>

      {vista === 'tableros' && (
        <>
          {(cargandoTableros || (cargandoTablero && !tablero)) && (
            <div className="py-16 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>
          )}
          {isError && <p className="py-12 text-center text-sm text-text-secondary">No se pudo cargar el tablero.</p>}
          {tablero && (
            <KanbanBoard
              tablero={tablero}
              onAbrirTarea={abrirTarea}
              onNuevaTarea={columnaId => setNuevaTarea({ columnaId })}
            />
          )}
        </>
      )}
      {vista === 'mias' && <MisTareasView onAbrirTarea={abrirTarea} />}
      {vista === 'lista' && <ListaTareasView onAbrirTarea={abrirTarea} />}

      <TareaFormDialog
        open={!!nuevaTarea}
        onOpenChange={v => { if (!v) setNuevaTarea(null); }}
        tableroId={vista === 'tableros' ? tableroId : undefined}
        columnaId={nuevaTarea?.columnaId}
      />
      <TareaDetailModal tareaId={tareaAbiertaId} onClose={cerrarTarea} onAbrirTarea={abrirTarea} />
      {esAdmin && (
        <AdministrarTablerosDialog
          open={administrando}
          onOpenChange={setAdministrando}
          tableroActualId={tableroId}
          onTableroCreado={setTableroId}
        />
      )}
    </div>
  );
}
